import { Link } from "react-router-dom";
import { metaForType, shortTime, conversationTitle } from "@/lib/messagingTypes";
import { parseMentions } from "@/lib/mentions";

/**
 * Single inbox row. `basePath` is the route prefix for the thread link
 * (e.g. "/parent/messages"), so this component is shared across roles.
 */
export default function ConversationRow({
  conversation,
  basePath,
  helpers,
  senderLabel,
}) {
  const meta = metaForType(conversation.type);
  const Icon = meta.icon;
  const last = conversation.last_message;
  const title = conversationTitle(conversation, helpers);
  const unread = conversation.unread_count || 0;
  const preview = last?.body || "No messages yet.";

  return (
    <Link
      to={`${basePath}/${conversation.id}`}
      data-testid={`conversation-row-${conversation.id}`}
      data-conversation-type={conversation.type}
      className={`flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-4 transition-all hover:-translate-y-[1px] hover:shadow-sm ${
        unread > 0 ? "border-primary/40" : ""
      }`}
    >
      <div
        className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${meta.bg}`}
      >
        <Icon size={20} weight="duotone" className={meta.color} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate font-heading text-base font-semibold tracking-tight text-neutral-900">
            {title}
          </p>
          <p className="flex-shrink-0 text-xs text-neutral-500">
            {shortTime(last?.created_at || conversation.last_message_at)}
          </p>
        </div>
        <p className="overline mt-1 text-neutral-500">
          {meta.label}
          {senderLabel ? ` · ${senderLabel}` : ""}
        </p>
        <p
          className={`mt-1 line-clamp-2 text-sm ${
            unread > 0 ? "font-medium text-neutral-900" : "text-neutral-600"
          }`}
        >
          {parseMentions(preview).map((segment, index) =>
            segment.type === "mention" ? (
              <span key={index} className="font-semibold text-primary-hover">
                @{segment.name}
              </span>
            ) : (
              <span key={index}>{segment.text}</span>
            ),
          )}
        </p>
      </div>
      {unread > 0 && (
        <span
          data-testid={`conversation-unread-${conversation.id}`}
          aria-label={`${unread} unread`}
          className="ml-1 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-primary px-2 text-[11px] font-bold text-white"
        >
          {unread}
        </span>
      )}
    </Link>
  );
}
